import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { formatMoney } from '@his/shared';
import { put } from '@/lib/api';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { LimitFields, readLimits } from './TenantsPage';
import { useOwnerCapability } from './owner-nav';

export interface FacilityBilling {
  priceMinor: number;
  billingCycle: string;
  setupFeeMinor: number;
  limits: { users: number | null; beds: number | null; branches: number | null; storageMb: number | null; smsCredits: number | null };
}

type Editing = 'commercial' | 'limits' | null;

export function TenantBillingPanel({
  tenantId,
  slug,
  currency,
  billing,
}: {
  tenantId: string;
  slug: string;
  currency: string;
  billing: FacilityBilling;
}) {
  const money = (minor: number) => formatMoney(minor, currency);
  const queryClient = useQueryClient();
  const canCommercial = useOwnerCapability('tenant.commercial.update');
  const canLimits = useOwnerCapability('tenant.limits.update');
  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState(() => toForm(billing));

  const start = (mode: Editing) => {
    setForm(toForm(billing));
    setEditing(mode);
  };
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    setEditing(null);
  };

  const limits = billing.limits ?? {};
  const shown = [
    ['Users', limits.users],
    ['Beds', limits.beds],
    ['Branches', limits.branches],
    ['Storage (MB)', limits.storageMb],
    ['SMS credits', limits.smsCredits],
  ] as const;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">What this facility pays</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-lg font-semibold tabular">
                {money(billing.priceMinor)}{' '}
                <span className="text-sm font-normal text-muted-foreground">/ {humanize(billing.billingCycle, 'billing_cycle')}</span>
              </p>
              {billing.setupFeeMinor > 0 && (
                <p className="text-sm text-muted-foreground">One-off setup fee {money(billing.setupFeeMinor)}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {canCommercial && (
                <Button variant="outline" size="sm" onClick={() => start('commercial')}>
                  Change price
                </Button>
              )}
              {canLimits && (
                <Button variant="outline" size="sm" onClick={() => start('limits')}>
                  Change limits
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {shown.map(([label, value]) => (
              <Badge key={label} variant={value === null || value === undefined ? 'muted' : 'secondary'}>
                {label}: {value === null || value === undefined ? 'Unlimited' : value}
              </Badge>
            ))}
          </div>

          <Alert>
            The plan decides the modules and is the same for every facility of this kind. The price and the
            limits are this facility’s own.
          </Alert>
        </CardContent>
      </Card>

      {editing === 'commercial' && (
        <OwnerConfirmDialog
          open
          onClose={() => setEditing(null)}
          title="Change what this facility pays"
          description={slug}
          confirmLabel="Save"
          successMessage="Price saved"
          wide
          action={(payload) =>
            put(`/platform/tenants/${tenantId}/commercial`, {
              ...payload,
              priceMinor: Math.round(Number(form.price || 0) * 100),
              billingCycle: form.billingCycle as 'monthly' | 'annual',
              setupFeeMinor: Math.round(Number(form.setupFee || 0) * 100),
            })
          }
          onDone={done}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Price">
              <Input
                type="number"
                step="0.01"
                min={0}
                className="tabular"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </Field>
            <Field label="Billing cycle">
              <ChoiceButtons
                value={form.billingCycle}
                onChange={(value) => setForm({ ...form, billingCycle: value })}
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'annual', label: 'Annual' },
                ]}
              />
            </Field>
            <Field label="Setup fee">
              <Input
                type="number"
                step="0.01"
                min={0}
                className="tabular"
                value={form.setupFee}
                onChange={(e) => setForm({ ...form, setupFee: e.target.value })}
              />
            </Field>
          </div>
        </OwnerConfirmDialog>
      )}

      {editing === 'limits' && (
        <OwnerConfirmDialog
          open
          onClose={() => setEditing(null)}
          title="Change this facility’s limits"
          description={slug}
          confirmLabel="Save"
          successMessage="Limits saved"
          wide
          action={(payload) => put(`/platform/tenants/${tenantId}/limits`, { ...payload, limits: readLimits(form) })}
          onDone={done}
        >
          <div>
            <p className="mb-2 text-xs text-muted-foreground">Leave blank for unlimited.</p>
            <LimitFields form={form} onChange={(patch) => setForm({ ...form, ...patch })} />
          </div>
        </OwnerConfirmDialog>
      )}
    </>
  );
}

function toForm(billing: FacilityBilling): Record<string, string> {
  const limits = billing.limits ?? {};
  return {
    price: String(billing.priceMinor / 100),
    billingCycle: billing.billingCycle,
    setupFee: String(billing.setupFeeMinor / 100),
    users: limits.users ? String(limits.users) : '',
    beds: limits.beds ? String(limits.beds) : '',
    branches: limits.branches ? String(limits.branches) : '',
    storageMb: limits.storageMb ? String(limits.storageMb) : '',
    smsCredits: limits.smsCredits ? String(limits.smsCredits) : '',
  };
}
